<template>
  <div :class="{ header: !isCart, 'header-cart': isCart }">
    <div class="loader-container" v-if="loadingUserClient">
      <LoadingState :size="20" />
    </div>
    <div class="header-inner" v-if="!loadingUserClient">
      <div class="header-menu-options">
        <template v-if="showLogo">
          <NavLogo v-if="!isCart" />
          <NavLogoFull v-else />
        </template>
        <div
          class="header-client-container header-menu-user-options client"
          v-if="client && (!userLoginableItems || userLoginableItems.length < 2)"
        >
          Client: {{ client.name }}
        </div>
        <DropDown
          :items="userLoginableItems"
          v-if="client && userLoginableItems && userLoginableItems.length > 1"
          :disabled="switchingClient"
          testId="switch-client-drop-down"
        >
          <div class="header-client-container header-menu-user-options client" tabindex="10">
            <div class="client-label">Client: {{ client.name }}</div>
            <font-awesome-icon icon="fa-solid fa-chevron-down" />
          </div>
        </DropDown>
      </div>
      <div class="header-account-info" v-if="client">
        <div class="id-container tw-flex tw-items-center">Account ID {{ client.id }}</div>
        <div class="pin-container tw-items-center">
          PIN: {{ pin }}
          <IconButton
            @click="toggleShowPin"
            :icon="{
              default: { icon: `fa-regular ${showPin ? 'fa-eye-slash' : 'fa-eye'}` },
              hover: { icon: `fa-solid ${showPin ? 'fa-eye-slash' : 'fa-eye'}` },
            }"
            class="pinToggle tw-text-[18px]"
            data-test="show-account-pin"
          />
        </div>
      </div>
      <div class="header-menu-options" v-if="client">
        <DropDown :items="userMenuItems" v-if="user" testId="user-drop-down-menu">
          <div class="header-menu-user-options" tabindex="10">
            <div class="avatar-circle">
              <div class="avatar-initials">{{ userInitials }}</div>
            </div>
            <div class="greeting">
              <div class="name">
                <template v-if="user && user.firstName"> Hi, {{ user.firstName }}! </template>
                <template v-else>User Menu</template>
              </div>
              <font-awesome-icon icon="fa-solid fa-chevron-down" />
            </div>
          </div>
        </DropDown>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from "vue";
import { constants } from "@/utils";
import DropDown from "@/components/display/DropDown";
import IconButton from "@/components/display/IconButton.vue";
import LoadingState from "@/components/display/LoadingState";
import NavLink from "@/classes/NavLink";
import NavLogo from "@/components/nav/NavLogo";
import NavLogoFull from "@/components/nav/NavLogoFull";
import router from "@/router";
import store from "@/store";

export default {
  name: "NavTopAuth",
  components: {
    DropDown,
    IconButton,
    LoadingState,
    NavLogo,
    NavLogoFull,
  },
  props: {
    user: Object,
    client: Object,
    userMenuItems: {
      validator: (value) => {
        if (!Array.isArray(value)) {
          return false;
        }

        return value.filter((item) => !(item instanceof NavLink)).length === 0;
      },
    },
    userLoginableItems: Array,
  },
  setup(props) {
    const showLogo = computed(() => store.getters["nav/hideSidebar"]);
    const userInitials = computed(() => {
      if (props.user) {
        if (props.user.firstName && props.user.lastName) {
          return props.user.firstName.slice(0, 1) + props.user.lastName.slice(0, 1);
        }
        return props.user.email.slice(0, 1);
      }
      return null;
    });

    const userGreeting = computed(() => {
      if (props.user) {
        if (props.user.firstName) {
          return `${props.user.firstName}`;
        }
        return `${props.user.email}`;
      }
      return null;
    });

    // PIN functionality
    const showPin = ref(false);
    const pin = computed(() => {
      if (showPin.value) {
        return props.client.pin;
      }
      return "• • • • • •";
    });

    const toggleShowPin = () => {
      showPin.value = !showPin.value;
    };

    // const openLegal = () => {
    //   window.open("https://www.nexcess.net/policies/", "_blank", "noopener, noreferrer");
    // };

    const formatDataTest = (input) => {
      return input.toLowerCase().replace(/(,|_| |;)/g, "-");
    };

    const loadingUserClient = computed(() => {
      return store.getters["auth/isLoading"];
    });

    const switchingClient = computed(() => {
      return store.getters["auth/isSwitching"];
    });

    const isCart = computed(() => router?.currentRoute?.value?.meta?.cart);

    return {
      formatDataTest,
      KB_LINKS: constants.LINKS.NEXCESS.KB,
      loadingUserClient,
      pin,
      showLogo,
      showPin,
      switchingClient,
      toggleShowPin,
      userGreeting,
      userInitials,
      isCart,
    };
  },
};
</script>

<style lang="scss">
@import "~@/styles/variables";

// .header-cart {
//   height: $header-cart-height;
// }

// .header {
//   height: $header-height;
// }

.header,
.header-cart {
  // height: $header-height;
  font-size: 12px;

  .header-inner {
    // padding: 0 0 0 $grid-gutter-width;
    height: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  .header-client-container {
    font-size: 0.75rem;
  }

  .header-account-info {
    display: flex;
    flex-direction: row;
    .id-container {
      margin-right: 2rem;
    }
    .pin-container {
      display: flex;

      .pinToggle {
        margin-left: 12px;
      }

      .iconToggle:hover {
        cursor: pointer;
      }
    }
  }

  .header-menu-options {
    display: flex;
    align-items: center;

    .header-menu-icons {
      display: flex;
      align-items: center;
      margin-right: 1rem;

      .header-menu-icon {
        svg {
          padding: 5px;
        }
        &.active {
          border-radius: $border-radius;
        }
        &:hover {
          border-radius: $border-radius;
        }
      }
    }

    .header-menu-user-options {
      &.client {
        font-size: 12px;
        .client-label {
          margin-right: 1rem;
        }
      }
      display: flex;
      flex-direction: row;
      position: relative;
      height: 100%;
      align-items: center;
      cursor: pointer;

      &:focus {
        outline: none;
      }

      .header-menu-list {
        position: absolute;
        display: none;
      }

      .avatar-circle {
        width: 1.5rem;
        height: 1.5rem;
        display: flex;
        justify-content: center;
        align-items: center;
        border-width: 1px;
        border-style: solid;
        border-radius: 1rem;
        margin-right: 8px;
        .avatar-initials {
          text-transform: uppercase;
        }
      }

      .greeting {
        display: flex;
        flex-direction: row;
        align-items: center;

        .name {
          margin-right: 6px;
          text-decoration: underline;
          text-underline-offset: 3px;
        }
      }

      &:hover,
      &:focus,
      &.forceshow {
        .header-menu-list {
          display: block;
          border-radius: $border-radius;
          width: 200px;
          top: 100%;
          right: 0;
          padding: 12px 0;
          z-index: 100;

          .menu-list-item {
            padding: 7px 14px;
            font-size: 0.875rem;
            text-decoration: none;
            width: 100%;
            display: block;

            &:hover {
              text-decoration: none;
            }
          }
        }
      }
    }
  }
}
</style>
